<template>
  <div class="email-tag-input">
    <div class="email-tag-input__field" @click="focusInput">
      <sp-chip
        v-for="(tag, index) in tags"
        :key="tag.email"
        closable
        :label="tag.label"
        @close="emit('remove-tag', index)"
      ></sp-chip>
      <input
        ref="tagInputEl"
        class="email-tag-input__tag-field"
        :value="inputText"
        :placeholder="placeholder"
        @input="handleInput"
        @keydown.enter="handleCommitKey"
        @keydown.tab="handleCommitKey"
        @keydown.space="handleSpaceKey"
        @keydown.backspace="handleBackspaceKey"
        @keydown="handleDelimiterKey"
        @paste="handleTagPaste"
        @blur="emit('commit')"
      />
    </div>

    <span class="email-tag-input__tag-count">
      {{ tagCountText }}
    </span>
  </div>
</template>

<script setup>
import { useTemplateRef } from "vue";
import { DELIMITERS, EMAIL_REGEX, NAMED_EMAIL_REGEX } from "#ce/apps/SpAdmin/SpRsvpInviteApp/utils/emailParser";

const emit = defineEmits(["update-input-text", "remove-tag", "commit"]);

const props = defineProps({
  tags: {
    type: Array,
    default: () => [],
  },
  inputText: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  tagCountText: {
    type: String,
    default: "",
  },
});

const tagInputEl = useTemplateRef("tagInputEl");

function focusInput() {
  tagInputEl.value?.focus();
}

function handleInput(event) {
  emit("update-input-text", event.target.value);
}

function handleCommitKey(event) {
  const text = event.target.value.trim();

  if (text) {
    event.preventDefault();
    emit("commit");
  }
}

function handleSpaceKey(event) {
  const text = event.target.value.trim();

  if (text && (EMAIL_REGEX.test(text) || NAMED_EMAIL_REGEX.test(text))) {
    event.preventDefault();
    emit("commit");
  }
}

function handleBackspaceKey(event) {
  if (!event.target.value) {
    emit("remove-tag", props.tags.length - 1);
  }
}

function handleDelimiterKey(event) {
  if (DELIMITERS.includes(event.key)) {
    event.preventDefault();
    emit("commit");
  }
}

function handleTagPaste(event) {
  event.preventDefault();
  const pasted = event.clipboardData?.getData("text") || "";

  emit("commit", `${event.target.value}${pasted}`);
}
</script>

<style scoped lang="scss">
.email-tag-input {
  &__field {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: flex-start;
    gap: var(--sp-ref-scale-4);
    min-height: 10rem;
    max-height: 12rem;
    overflow-y: auto;
    padding: var(--sp-ref-scale-8);
    border: var(--sp-ref-spacing-px) solid var(--sp-sys-color-outline, #ccc);
    border-radius: var(--sp-ref-scale-4);
    box-sizing: border-box;
    cursor: text;

    &:focus-within {
      outline: var(--sp-ref-scale-2) solid var(--sp-sys-color-primary, #1976d2);
      outline-offset: calc(var(--sp-ref-spacing-px) * -1);
    }
  }

  &__tag-field {
    flex: 1 1 12rem;
    min-width: 8rem;
    border: none;
    outline: none;
    padding: var(--sp-ref-scale-2) 0;
    font-family: var(--sp-sys-typescale-body-font-family, inherit);
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    line-height: 1.25rem;
    background: transparent;

    &::placeholder {
      color: var(--sp-sys-color-on-surface-variant, #666);
      font-style: italic;
      opacity: 0.6;
    }
  }

  &__tag-count {
    display: block;
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    line-height: 1.25rem;
    min-height: 1.25rem;
    color: var(--sp-sys-color-on-surface-variant, #666);
    margin-top: var(--sp-ref-scale-4);
  }
}
</style>
